import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { z } from 'zod';
import React from 'react';
import { toast } from 'sonner';
import { captureException } from '@sentry/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { doc, Timestamp, collection, writeBatch } from '@firebase/firestore';
import { Controller, FormProvider, useForm, SubmitErrorHandler } from 'react-hook-form';

import type { CustomStep, HealthCategoryId, StatusActivity } from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config.js';
import { useAuth } from '../../../../hooks/index.js';
import { metadataConfig } from './metadataConfig.ts';
import { MetadataFormController } from './MetadataFormController.tsx';
import { type CustomStepTemplate, TemplateSelector } from './TemplateSelector';

type CustomStepCreatorProps = {
  open: boolean;
  patientId: string;
  healthCategoryId: HealthCategoryId;
  onClose: () => void;
  onSuccess: () => void;
};

const typesSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('DOCUMENT_UPLOAD'),
    metadata: z.object({
      documentType: z.custom<CustomStep['DOCUMENT_UPLOAD']['metadata']['documentType']>(),
    }),
  }),
  z.object({
    type: z.literal('MESSAGE'),
    metadata: z.object({
      messageContext: z.custom<CustomStep['MESSAGE']['metadata']['messageContext']>(),
    }),
  }),
  z.object({
    type: z.literal('DISCHARGE_LETTER_UPLOAD'),
    metadata: z.object({}),
  }),
]);

const validationSchema = z
  .object({
    title: z.string().min(1, 'Title is required'),
    description: z.string(),
  })
  .and(typesSchema);

export type CustomStepFormData = z.infer<typeof validationSchema>;

export const CustomStepCreator: React.FC<CustomStepCreatorProps> = ({
  open,
  patientId,
  healthCategoryId,
  onClose,
  onSuccess,
}) => {
  const { user, userType } = useAuth();

  const methods = useForm<CustomStepFormData>({
    defaultValues: {
      title: '',
      description: '',
      type: 'MESSAGE',
      metadata: metadataConfig.MESSAGE.initialValue,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    control,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    reset,
    setValue,
    setError,
  } = methods;

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleTemplateSelect = (template: CustomStepTemplate) => {
    const { stepData } = template.getData({ healthCategoryId });

    setValue('type', template.type, { shouldValidate: true });
    setValue('title', stepData.title, { shouldValidate: true });
    setValue('description', stepData.description, { shouldValidate: true });
    setValue('metadata', stepData.metadata, { shouldValidate: true });
  };

  const handleOnSubmit = async (data: CustomStepFormData) => {
    try {
      if (!user?.uid || !userType) {
        throw new Error('Please login to create a custom step');
      }

      const docRef = doc(collection(db, 'patients', patientId, 'intakeStatus'));
      const statusActivityRef = doc(collection(db, 'patients', patientId, 'intakeStatusActivity'));

      const now = Timestamp.now();

      const batch = writeBatch(db);

      type Step = CustomStep[typeof data.type];
      type StepCurrentStatus = NonNullable<Step['currentStatus']>;

      const actionedBy = {
        userType,
        id: user.uid,
        email: user.email ?? '',
        name: user.displayName,
      } satisfies StepCurrentStatus['actionedBy'];

      const currentStatus = {
        actionedBy,
        note: null,
        metadata: null,
        actionedAt: now,
        status: 'NOT_STARTED',
      } satisfies StepCurrentStatus;

      const newStep = {
        ...data,
        currentStatus,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
        healthCategoryId,
        isDeleted: false,
        isDisabled: false,
        createdByUid: user.uid,
        stepType: 'CUSTOM_STEP',
      } satisfies Step;

      batch.set(docRef, newStep);

      batch.set(statusActivityRef, {
        healthCategoryId,
        stepId: docRef.id,
        stepSnapshot: newStep,
        action: 'CREATE_STEP',
        actionedAt: now,
        actionedBy,
        metadata: {},
      } satisfies StatusActivity<'CREATE_STEP'>);

      await batch.commit();

      toast.success('Custom step created successfully');
      onSuccess();
      handleClose();
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error creating custom step:', err);
      }

      captureException(err, {
        tags: { issueIn: 'CustomStepCreator.handleOnSubmit' },
        extra: { data },
      });

      setError('root', {
        type: 'manual',
        message: 'Failed to create custom step. Please try again.',
      });

      toast.error('Failed to create custom step');
    }
  };

  const handleOnSubmitError: SubmitErrorHandler<CustomStepFormData> = (errors) => {
    if (import.meta.env.DEV) {
      console.error('Form validation errors:', errors);
    }

    toast.error('Please fix the form errors and try again');
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleOnSubmit, handleOnSubmitError)}>
          <DialogTitle component="div">
            <Typography variant="h6">Add Custom Step</Typography>
            <Typography variant="body2">
              Create a custom step for the patient's intake process for their {healthCategoryId} form.
            </Typography>
          </DialogTitle>
          <DialogContent>
            {errors.root && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.root.message}
              </Alert>
            )}

            <Box sx={{ mb: 2, mt: 1 }}>
              <TemplateSelector healthCategoryId={healthCategoryId} onTemplateSelect={handleTemplateSelect} />
            </Box>

            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  disabled={isSubmitting}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isSubmitting}
                />
              )}
            />

            <MetadataFormController />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
            >
              Create Step
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
